import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  analyzeOffGridSizing,
  fetchOffGridScope,
  hasLoadData,
  isEmptyScopeError,
} from "../services/offGridCalculatorApi";
import { useBranch } from "../context/BranchContext";
import { formatReportDateShort } from "../utils/energyReportData";
import { parseApiError } from "../utils/parseApiError";
import { ApiErrorAlert } from "../components/ErrorComponents";
import FieldHelp from "../help/FieldHelp";
import "./OffGridCalculatorTab.css";

const ALL_BRANCHES = "__all__";
const MODE_ALL_MAINS = "all_mains";
const MODE_CUSTOM = "custom";
const MAIN_SLOTS = [1, 2];
const SIZING_WORST = "worst";

const describeEmptyScope = (branch, mainLabel) => {
  const parts = [branch && branch !== "All" ? branch : null, mainLabel].filter(Boolean);
  const where = parts.length ? parts.join(" · ") : "the selected scope";
  return `No machines are connected under ${where} yet, so there is nothing to size.`;
};

const categoryLabel = (category, isMain) => {
  if (isMain || category === "GRID") return "Main";
  if (category === "MACHINE" || category === "LOAD") return "Machine";
  return category || "Load";
};

const getMachineColor = (index) => {
  return tileColors[index % tileColors.length];
};

const tileColors = [
  { bg: "#f0fdf4", border: "rgba(16, 185, 129, 0.3)", accent: "#10b981", icon: "🟢" }, // Green
  { bg: "#fffbeb", border: "rgba(250, 204, 21, 0.3)", accent: "#eab308", icon: "🟡" }, // Yellow
  { bg: "#f5f3ff", border: "rgba(168, 85, 247, 0.3)", accent: "#a855f7", icon: "🟣" }, // Purple
  { bg: "#ecf9ff", border: "rgba(34, 211, 238, 0.3)", accent: "#22d3ee", icon: "🔵" }, // Cyan
  { bg: "#fef2f2", border: "rgba(239, 68, 68, 0.3)", accent: "#ef4444", icon: "🔴" }, // Red
  { bg: "#fffaeb", border: "rgba(251, 191, 36, 0.3)", accent: "#f59e0b", icon: "🟠" }, // Amber
];

let tileColorIndex = 0;

const ResultTile = ({ label, helpId, value, detail, index }) => {
  const colorSet = tileColors[index % tileColors.length];
  return (
    <div style={{...styles.resultTile, background: colorSet.bg, borderColor: colorSet.accent, borderWidth: "1.5px"}} className="offgrid-result-tile">
      <span style={styles.resultLabel}>
        {helpId ? <FieldHelp id={helpId}>{label}</FieldHelp> : label}
      </span>
      <strong style={{...styles.resultValue, color: colorSet.accent}}>{value}</strong>
      {detail && <span style={styles.resultDetail}>{detail}</span>}
    </div>
  );
};

const fmt = (value) => Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 });
const fmtKwh = (wh) => `${fmt((Number(wh) || 0) / 1000)} kWh`;
const fmtKwp = (wp) => `${fmt((Number(wp) || 0) / 1000)} kWp`;
const fmtKw = (w) => `${fmt((Number(w) || 0) / 1000)} kW`;

const SolarSystemSection = ({ solarSystem }) => {
  if (!solarSystem) return null;

  const { solarWp, batteryWh, inverterW } = solarSystem;

  return (
    <div style={styles.solarSection}>
      <h3 style={styles.solarSectionTitle}>
        <FieldHelp id="offGrid.recommendedSystem">Recommended Solar System</FieldHelp>
      </h3>
      <div style={styles.resultsGrid}>
        <ResultTile index={0} label="Solar panels" helpId="offGrid.solarPanels" value={fmtKwp(solarWp)} />
        <ResultTile index={1} label="Battery" helpId="offGrid.battery" value={fmtKwh(batteryWh)} />
        <ResultTile index={2} label="Inverter" helpId="offGrid.inverter" value={fmtKw(inverterW)} />
      </div>
    </div>
  );
};

const SizingBlock = ({ result }) => {
  const { name, badge, machineNames, loadAnalysis, solarSystem, error } = result || {};

  if (error || !loadAnalysis || !hasLoadData(loadAnalysis)) {
    return (
      <div style={styles.mainBlock}>
        <div style={styles.mainBlockHead}>
          <span>{name}</span>
          {badge && <span style={styles.badge}>{badge}</span>}
        </div>
        <p style={styles.muted}>{error || "No energy data in the past year."}</p>
      </div>
    );
  }

  return (
    <div style={styles.mainBlock} className="offgrid-result-block">
      <div style={styles.mainBlockHead}>
        <span>{name}</span>
        {badge && <span style={styles.badge}>{badge}</span>}
      </div>

      <SolarSystemSection solarSystem={solarSystem} />
    {machineNames?.length > 0 && (
        <div style={styles.machineNameContainer}>
          {machineNames.map((name, idx) => {
            const colorSet = getMachineColor(idx);
            return (
              <span
                key={idx}
                style={{
                  ...styles.machineNameBadge,
                  background: colorSet.bg,
                  borderColor: colorSet.border,
                  color: "#475569",       // Soft slate gray-black text
                  fontWeight: "normal",   // Explicitly overrides bold styles
                }}
              >
                {name}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};
const OffGridCalculatorTab = () => {
  const username = localStorage.getItem("username") || "";
  const { selectedBranch, branchList } = useBranch();
  const [localBranch, setLocalBranch] = useState("");

  const [scope, setScope] = useState(null);
  const [scopeLoading, setScopeLoading] = useState(true);
  const [scopeError, setScopeError] = useState(null);

  const [viewMode, setViewMode] = useState(MODE_ALL_MAINS);
  const [mainSlotFilter, setMainSlotFilter] = useState("1");
  const [sizingCase, setSizingCase] = useState(SIZING_WORST);

  const [mainCombinedResult, setMainCombinedResult] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [analyzeError, setAnalyzeError] = useState(null);
  const [emptyNotice, setEmptyNotice] = useState(null);

  const [selectedNames, setSelectedNames] = useState(new Set());
  const [customResult, setCustomResult] = useState(null);
  const [customLoading, setCustomLoading] = useState(false);

  const availableBranches = useMemo(() => {
    let list = [];
    if (branchList && branchList.length > 0) {
      list = branchList;
    } else if (scope?.branches && scope.branches.length > 0) {
      list = scope.branches.map((b) => (typeof b === "string" ? { name: b } : b));
    }
    return list.filter((b) => {
      const name = (b.name || b.id || "").trim().toLowerCase();
      return name && name !== "all" && name !== "all branches" && name !== "all branch" && name !== "__all__";
    });
  }, [branchList, scope?.branches]);

  // When branches load or global selection changes, set default local branch to first available specific branch
  useEffect(() => {
    if (availableBranches.length > 0) {
      if (!localBranch || !availableBranches.some((b) => b.name === localBranch)) {
        setLocalBranch(availableBranches[0].name);
      }
    }
  }, [availableBranches, localBranch, selectedBranch]);

  // If a specific branch is selected in the top bar, use it; otherwise use the in-page dropdown selection
  const activeBranch =
    selectedBranch !== "All"
      ? selectedBranch
      : localBranch || (availableBranches[0]?.name ?? "");
  const branchFilter =
    activeBranch && activeBranch !== "All"
      ? activeBranch
      : availableBranches[0]?.name ?? ALL_BRANCHES;

  const loadScope = useCallback(async () => {
    if (!username) {
      setScopeLoading(false);
      setScopeError("Please log in to use the calculator.");
      return;
    }

    setScopeLoading(true);
    setScopeError(null);
    try {
      const scopeMainSlot = viewMode === MODE_CUSTOM ? "all" : mainSlotFilter;
      const data = await fetchOffGridScope(username, branchFilter, scopeMainSlot);
      setScope(data);
    } catch (err) {
      console.error("Scope fetch failed:", err);
      const parsed = err.parsedError || parseApiError(err);
      setScopeError({ ...parsed, retryable: true });
      setScope(null);
    } finally {
      setScopeLoading(false);
    }
  }, [username, branchFilter, mainSlotFilter, viewMode]);

  useEffect(() => {
    loadScope();
  }, [loadScope]);

  useEffect(() => {
    if (viewMode === MODE_ALL_MAINS) {
      setMainSlotFilter("1");
    }
  }, [branchFilter, viewMode]);

  const machinesInScope = scope?.machines ?? [];
  const mainsToSize = scope?.mains ?? [];
  const hasAnyMains = scope?.hasAnyMains ?? false;
  const dateRange = scope?.dateRange;

  const loadAllMains = useCallback(async () => {
    if (!username || viewMode !== MODE_ALL_MAINS) return;

    setAnalyzeError(null);
    setEmptyNotice(null);
    setMainCombinedResult(null);

    // Nothing connected under this main is a normal state, not a failed
    // request -- don't call analyze just to render an error.
    if (machinesInScope.length === 0) {
      setEmptyNotice(describeEmptyScope(activeBranch, `Main ${mainSlotFilter}`));
      return;
    }

    setBulkLoading(true);
    try {
      const data = await analyzeOffGridSizing({
        username,
        mode: MODE_ALL_MAINS,
        branch: branchFilter,
        mainSlot: mainSlotFilter,
        sizingCase,
        combine: true,
      });
      if (data.empty || !data.combined) {
        setEmptyNotice(data.message || describeEmptyScope(activeBranch, `Main ${mainSlotFilter}`));
        setMainCombinedResult(null);
        return;
      }
      setMainCombinedResult({ ...data.combined, name: `Main ${mainSlotFilter}` });
    } catch (err) {
      setMainCombinedResult(null);
      if (isEmptyScopeError(err)) {
        setEmptyNotice(describeEmptyScope(activeBranch, `Main ${mainSlotFilter}`));
        return;
      }
      console.error("Bulk sizing failed:", err);
      const parsed = err.parsedError || parseApiError(err);
      setAnalyzeError({ ...parsed, retryable: true });
    } finally {
      setBulkLoading(false);
    }
  }, [
    username,
    viewMode,
    branchFilter,
    mainSlotFilter,
    sizingCase,
    machinesInScope.length,
    activeBranch,
  ]);

  useEffect(() => {
    if (scopeLoading || !scope || viewMode !== MODE_ALL_MAINS) return;
    loadAllMains();
  }, [scopeLoading, scope, viewMode, loadAllMains]);

  const loadCustomSelection = useCallback(async () => {
    const names = Array.from(selectedNames);
    if (!username || !names.length) {
      setCustomResult(null);
      return;
    }

    setCustomLoading(true);
    setCustomResult(null);
    setAnalyzeError(null);
    setEmptyNotice(null);
    try {
      const data = await analyzeOffGridSizing({
        username,
        mode: MODE_CUSTOM,
        branch: branchFilter,
        machineNames: names,
        sizingCase,
        combine: true,
      });
      const result = data.combined ?? data.results?.[0] ?? null;
      if (data.empty || !result) {
        setEmptyNotice(data.message || describeEmptyScope(activeBranch, null));
        setCustomResult(null);
        return;
      }
      setCustomResult(result);
    } catch (err) {
      setCustomResult(null);
      if (isEmptyScopeError(err)) {
        setEmptyNotice(describeEmptyScope(activeBranch, null));
        return;
      }
      console.error("Custom sizing failed:", err);
      const parsed = err.parsedError || parseApiError(err);
      setAnalyzeError({ ...parsed, retryable: true });
    } finally {
      setCustomLoading(false);
    }
  }, [username, selectedNames, branchFilter, sizingCase, activeBranch]);

  useEffect(() => {
    setSelectedNames(new Set());
    setCustomResult(null);
    setEmptyNotice(null);
  }, [branchFilter, viewMode]);

  useEffect(() => {
    if (viewMode !== MODE_CUSTOM || selectedNames.size === 0) return;
    loadCustomSelection();
  }, [sizingCase]);

  const toggleMachine = (name) => {
    setSelectedNames((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
    setCustomResult(null);
  };

  const selectAllMachines = () => {
    setSelectedNames(new Set(machinesInScope.map((m) => m.name)));
    setCustomResult(null);
  };

  const clearSelection = () => {
    setSelectedNames(new Set());
    setCustomResult(null);
  };

  const mainSlotLabel = `Main ${mainSlotFilter}`;

  const dateRangeLabel = useMemo(() => {
    if (!dateRange?.start || !dateRange?.end) return "past year";
    return `${formatReportDateShort(dateRange.start)} – ${formatReportDateShort(dateRange.end)}`;
  }, [dateRange]);

  return (
    <div style={styles.page} className="offgrid-page">
      <header style={styles.header} className="offgrid-header">
        <h1 style={styles.title} className="offgrid-title-gradient">Off-Grid Sizing</h1>
        <p style={styles.subtitle}>
          Based on the{" "}
          <FieldHelp id="offGrid.analysisPeriod">past year of meter data</FieldHelp>{" "}
          ({dateRangeLabel}), excluding solar & generator.
        </p>
      </header>

      <div style={styles.toolbar} className="offgrid-toolbar glass-panel">
        <div style={styles.modeToggle}>
          <button
            type="button"
            style={{
              ...styles.modeBtn,
              ...(viewMode === MODE_ALL_MAINS ? styles.modeBtnActive : {}),
            }}
            onClick={() => setViewMode(MODE_ALL_MAINS)}
          >
            Combined sizing
          </button>
          <button
            type="button"
            style={{
              ...styles.modeBtn,
              ...(viewMode === MODE_CUSTOM ? styles.modeBtnActive : {}),
            }}
            onClick={() => setViewMode(MODE_CUSTOM)}
          >
            Custom
          </button>
        </div>

        {/* In-page Branch Dropdown: only displayed if top bar has 'All' selected */}
        {selectedBranch === "All" && availableBranches.length > 0 && (
          <div style={styles.toolbarField}>
            <label>
              <FieldHelp id="offGrid.branch">Branch</FieldHelp>
            </label>
            <select
              value={activeBranch}
              onChange={(e) => setLocalBranch(e.target.value)}
              style={styles.select}
            >
              {availableBranches.map((b) => (
                <option key={b.id || b.name} value={b.name}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {viewMode === MODE_ALL_MAINS && hasAnyMains && (
          <div style={styles.mainSlotToggle}>
            {MAIN_SLOTS.map((slot) => (
              <button
                key={slot}
                type="button"
                style={{
                  ...styles.modeBtn,
                  ...(mainSlotFilter === String(slot) ? styles.modeBtnActive : {}),
                }}
                onClick={() => setMainSlotFilter(String(slot))}
              >
                Main {slot}
              </button>
            ))}
          </div>
        )}

      </div>

      {scopeLoading && <p style={styles.muted}>Loading facility hierarchy…</p>}
      {scopeError && (
        <ApiErrorAlert
          error={scopeError}
          title="Could not load facility scope"
          onRetry={loadScope}
        />
      )}
      {analyzeError && !scopeError && (
        <ApiErrorAlert
          error={analyzeError}
          title="Sizing analysis failed"
          onRetry={viewMode === MODE_ALL_MAINS ? loadAllMains : loadCustomSelection}
        />
      )}

      {!scopeLoading && !scopeError && (
        <div style={styles.layout}>
          <section style={styles.resultsPanel} className="offgrid-results-panel glass-panel">
            {viewMode === MODE_ALL_MAINS && (
              <>
                <div style={styles.resultsHead}>
                  <h2 style={styles.panelTitle}>
                    <FieldHelp id="offGrid.combinedSizing">Combined sizing</FieldHelp>
                  </h2>
                  <span style={styles.scopeTag} className="offgrid-scope-badge">
                    <FieldHelp id="offGrid.scope">
                      {activeBranch !== "All" ? `${activeBranch} · ` : ""}
                      {mainSlotLabel} · {machinesInScope.length} machine
                      {machinesInScope.length !== 1 ? "s" : ""}
                    </FieldHelp>
                  </span>
                </div>

                {bulkLoading && <p style={styles.muted}>Analyzing mains…</p>}

                {!bulkLoading && emptyNotice && (
                  <div style={styles.emptyState} className="offgrid-empty-state">
                    <span style={styles.emptyStateIcon} aria-hidden="true">⚡</span>
                    <p style={styles.emptyStateText}>{emptyNotice}</p>
                    <p style={styles.emptyStateHint}>
                      Pick another main or branch above, or connect a machine to this main.
                    </p>
                  </div>
                )}

                {!bulkLoading &&
                  mainCombinedResult &&
                  hasLoadData(mainCombinedResult.loadAnalysis) && (
                    <div style={styles.mainsList}>
                      <SizingBlock result={mainCombinedResult} />
                    </div>
                  )}

                {!bulkLoading &&
                  mainCombinedResult &&
                  !hasLoadData(mainCombinedResult.loadAnalysis) && (
                    <p style={styles.muted}>No energy data found for the past year.</p>
                  )}
              </>
            )}

            {viewMode === MODE_CUSTOM && (
              <>
                <div style={styles.resultsHead}>
                  <h2 style={styles.panelTitle}>
                    <FieldHelp id="offGrid.customSizing">Custom sizing</FieldHelp>
                  </h2>
                  {selectedNames.size > 0 && (
                    <span style={styles.scopeTag} className="offgrid-scope-badge">{selectedNames.size} machine(s)</span>
                  )}
                </div>

                {selectedNames.size === 0 && !emptyNotice && (
                  <p style={styles.muted}>
                    {machinesInScope.length === 0
                      ? "No machines are available in this branch yet."
                      : "Select one or more machines, then click Analyze."}
                  </p>
                )}

                {selectedNames.size > 0 && !customResult && !customLoading && (
                  <p style={styles.muted}>Click Analyze to load sizing for your selection.</p>
                )}

                {customLoading && <p style={styles.muted}>Analyzing selected machines…</p>}

                {!customLoading && emptyNotice && (
                  <div style={styles.emptyState} className="offgrid-empty-state">
                    <span style={styles.emptyStateIcon} aria-hidden="true">⚡</span>
                    <p style={styles.emptyStateText}>{emptyNotice}</p>
                    <p style={styles.emptyStateHint}>
                      Try a different branch, or pick machines that have reported data.
                    </p>
                  </div>
                )}

                {!customLoading && customResult && (
                  <div style={styles.mainsList}>
                    <SizingBlock result={customResult} />
                  </div>
                )}

                {!customLoading && selectedNames.size > 0 && customResult === null && analyzeError && !emptyNotice && (
                  <p style={styles.muted}>No energy data found for the past year.</p>
                )}
              </>
            )}
          </section>

          {viewMode === MODE_CUSTOM && (
            <section style={styles.machinePanel} className="offgrid-machine-panel glass-panel">
              <div style={styles.panelHead}>
                <h2 style={styles.panelTitle}>
                  <FieldHelp id="offGrid.selectMachines">Select machines</FieldHelp>
                </h2>
                <span style={styles.countTag} className="offgrid-count-badge">{selectedNames.size} selected</span>
              </div>

              <div style={styles.selectionActions}>
                <button type="button" style={styles.linkBtn} onClick={selectAllMachines}>
                  Select all
                </button>
                <button type="button" style={styles.linkBtn} onClick={clearSelection}>
                  Clear
                </button>
              </div>

              {machinesInScope.length === 0 ? (
                <p style={styles.muted}>No machines in this branch.</p>
              ) : (
                <div style={styles.machineList} className="offgrid-machine-list">
                  {machinesInScope.map((machine, index) => {
                    const isSelected = selectedNames.has(machine.name);
                    const badge = categoryLabel(machine.category, machine.isMain);
                    const colorSet = getMachineColor(index);
                    return (
                      <button
                        key={`${machine.name}-${index}`}
                        type="button"
                        onClick={() => toggleMachine(machine.name)}
                        style={{
                          ...styles.machineItem,
                          background: isSelected ? colorSet.bg : "#f8fafc",
                          borderColor: isSelected ? colorSet.border : "transparent",
                          ...(isSelected ? styles.machineItemActive : {}),
                          boxShadow: isSelected ? `0 0 0 2px ${colorSet.accent}20, 0 8px 20px ${colorSet.accent}20` : "none",
                          color: isSelected ? colorSet.accent : "#64748b",
                        }}
                      >
                        <span style={{fontSize: 20}}>{colorSet.icon}</span>
                        <span style={styles.machineName}>{machine.name}</span>
                        <span style={{...styles.badgeSmall, background: colorSet.accent + "20", color: colorSet.accent}}>{badge}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              <button
                type="button"
                style={styles.analyzeBtn}
                disabled={customLoading || selectedNames.size === 0}
                onClick={loadCustomSelection}
                className="offgrid-analyze-btn"
              >
                {customLoading ? "Analyzing…" : `Analyze ${selectedNames.size || ""} selected`}
              </button>
            </section>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  page: {
    padding: "28px 24px 40px",
    maxWidth: 1200,
    margin: "0 auto",
    background: "linear-gradient(135deg, #f8fafc 0%, #f1f8f6 100%)",
    minHeight: "100vh",
  },
  header: {
    marginBottom: 28,
    animation: "fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1)",
  },
  title: {
    margin: "0 0 8px",
    fontSize: 28,
    fontWeight: 600,
    color: "#0f172a",
    letterSpacing: "-0.01em",
  },
  subtitle: {
    margin: 0,
    color: "#64748b",
    fontSize: 15,
    lineHeight: 1.6,
    fontWeight: 500,
  },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    alignItems: "flex-end",
    marginBottom: 28,
    padding: "20px 24px",
    background: "rgba(255, 255, 255, 0.85)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.5)",
    borderRadius: 28,
    boxShadow: "0 12px 40px -12px rgba(0, 0, 0, 0.08)",
    transition: "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
  },
  modeToggle: {
    display: "flex",
    gap: 4,
    padding: 4,
    background: "#f1f5f9",
    borderRadius: 12,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
  },
  modeBtn: {
    padding: "8px 16px",
    border: "none",
    borderRadius: 10,
    background: "transparent",
    fontSize: 13,
    fontWeight: 700,
    color: "#64748b",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  modeBtnActive: {
    background: "#fff",
    color: "#1ba08e",
    boxShadow: "0 4px 12px rgba(27, 160, 142, 0.15)",
  },
  mainSlotToggle: {
    display: "flex",
    gap: 4,
    padding: 4,
    background: "rgba(27, 160, 142, 0.1)",
    borderRadius: 12,
  },
  toolbarField: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    fontSize: 11,
    fontWeight: 800,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    minWidth: 160,
  },
  select: {
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #e2e8f0",
    fontSize: 14,
    color: "#334155",
    background: "#fff",
    textTransform: "none",
    fontWeight: 500,
    letterSpacing: 0,
    transition: "all 0.2s ease",
    cursor: "pointer",
  },
  layout: {
    display: "flex",
    flexDirection: "column",
    gap: 20,
    alignItems: "stretch",
  },
  machinePanel: {
    background: "rgba(255, 255, 255, 0.85)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.5)",
    borderRadius: 28,
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    boxShadow: "0 12px 40px -12px rgba(0, 0, 0, 0.08)",
    transition: "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
  },
  panelHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  panelTitle: {
    margin: 0,
    fontSize: 14,
    fontWeight: 600,
    color: "#1e293b",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
  },
  countTag: {
    fontSize: 13,
    fontWeight: 600,
    color: "#fff",
    background: "linear-gradient(135deg, #1ba08e, #17a98b)",
    padding: "4px 12px",
    borderRadius: 999,
  },
  selectionActions: {
    display: "flex",
    gap: 12,
  },
  linkBtn: {
    padding: 0,
    border: "none",
    background: "none",
    color: "#1ba08e",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    textDecoration: "none",
    transition: "color 0.2s ease",
  },
  machineList: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
    alignItems: "center",
  },
  machineItem: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    padding: "8px 16px",
    border: "1.5px solid",
    borderRadius: 999,
    background: "#f8fafc",
    cursor: "pointer",
    textAlign: "center",
    fontSize: 12,
    color: "#1e293b",
    fontWeight: 500,
    transition: "all 0.25s ease",
    position: "relative",
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
  machineItemActive: {
    transform: "scale(1.1)",
    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.12)",
    fontWeight: 600,
  },
  checkBox: {
    width: 18,
    height: 18,
    flexShrink: 0,
    fontSize: 12,
    color: "#fff",
    background: "#1ba08e",
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
  },
  machineName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
    fontSize: 12,
    fontWeight: 500,
  },
  badgeSmall: {
    flexShrink: 0,
    fontSize: 8,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: "#0f766e",
    background: "#d0ebe8",
    padding: "1px 4px",
    borderRadius: 3,
  },
  analyzeBtn: {
    marginTop: 8,
    padding: "12px 18px",
    border: "none",
    borderRadius: 12,
    background: "linear-gradient(135deg, #1ba08e, #17a98b)",
    color: "#fff",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.2s ease",
    boxShadow: "0 4px 15px rgba(27, 160, 142, 0.3)",
  },
  resultsPanel: {
    background: "rgba(255, 255, 255, 0.85)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.5)",
    borderRadius: 28,
    padding: "24px",
    boxShadow: "0 12px 40px -12px rgba(0, 0, 0, 0.08)",
    transition: "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
  },
  resultsHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 20,
  },
  scopeTag: {
    fontSize: 13,
    fontWeight: 700,
    color: "#fff",
    background: "linear-gradient(135deg, #0f766e, #0a4d47)",
    padding: "4px 12px",
    borderRadius: 999,
  },
  mainsList: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  mainBlock: {
    background: "#ffffff",
    border: "1px solid rgba(27, 160, 142, 0.15)",
    borderRadius: 12,
    padding: "20px 24px",
    transition: "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
  },
  mainBlockHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 14,
    fontSize: 16,
    fontWeight: 600,
    color: "#0f172a",
  },
  badge: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "#1e40af",
    background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
    padding: "4px 10px",
    borderRadius: 8,
    boxShadow: "0 2px 8px rgba(30, 64, 175, 0.15)",
  },
  machineListNote: {
    margin: "0 0 14px",
    fontSize: 13,
    color: "#64748b",
    lineHeight: 1.6,
    fontWeight: 500,
  },
  machineNameContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    marginBottom: 16,
    borderTop: "1px solid rgba(27, 160, 142, 0.2)",
    marginBottom: 20,
    paddingTop: 12,
  },
  machineNameBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px 16px",
    borderRadius: 999,
    background: "#f0fdf4",
    border: "1.5px solid rgba(16, 185, 129, 0.3)",
    color: "#10b981",
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: "nowrap",
    boxShadow: "0 2px 6px rgba(16, 185, 129, 0.1)",
    transition: "all 0.2s ease",
  },
  resultsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 16,
  },
  resultTile: {
    background: "#ffffff",
    border: "1.5px solid",
    borderRadius: 12,
    padding: "18px 16px",
    transition: "all 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
    position: "relative",
    overflow: "hidden",
  },
  resultLabel: {
    display: "block",
    fontSize: 11,
    fontWeight: 600,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginBottom: 6,
  },
  resultValue: {
    display: "block",
    fontSize: 22,
    fontWeight: 700,
    color: "#1ba08e",
    letterSpacing: "-0.02em",
    marginBottom: 4,
  },
  resultDetail: {
    display: "block",
    marginTop: 8,
    fontSize: 11,
    color: "#64748b",
    lineHeight: 1.5,
    fontWeight: 500,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "6px",
    padding: "32px 20px",
    textAlign: "center",
  },
  emptyStateIcon: {
    fontSize: "28px",
    opacity: 0.55,
  },
  emptyStateText: {
    margin: 0,
    fontSize: "14px",
    fontWeight: 600,
    color: "#475569",
  },
  emptyStateHint: {
    margin: 0,
    fontSize: "12.5px",
    color: "#94a3b8",
  },
  analyzerSection: {
    marginBottom: 18,
  },
  analyzerTitle: {
    margin: "0 0 12px",
    fontSize: 13,
    fontWeight: 600,
    color: "#1ba08e",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  solarSection: {
    marginTop: 30,
    marginBottom: 30,
  },
  solarSectionTitle: {
    margin: "0 0 14px",
    fontSize: 13,
    fontWeight: 600,
    color: "#1ba08e",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  muted: {
    margin: 0,
    color: "#94a3b8",
    fontSize: 15,
    lineHeight: 1.6,
  },
  error: {
    margin: 0,
    color: "#dc2626",
    fontSize: 14,
  },
};

export default OffGridCalculatorTab;
